import React, { useState, useEffect } from "react";
import { Button, Alert, Table, Badge, Card, CardBody, Input, InputGroup, InputGroupText } from "reactstrap";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import { getConfig } from "../config";
import Loading from "../components/Loading";
import { fetchReceipts, fetchSuppliers, deleteReceipt, fetchReceiptFile, uploadReceipts } from "../utils/apiService";
import { useHistory, Link } from "react-router-dom";
import { useOrg } from "../utils/orgContext";

export const ReceiptsComponent = () => {
  const { apiOrigin = "http://localhost:3001", audience } = getConfig();
  const history = useHistory();
  const { orgId, ready } = useOrg();

  const [state, setState] = useState({
    showResult: false,
    receipts: [],
    filteredReceipts: [],
    apiMessage: "",
    error: null,
    loading: false,
    searchTerm: "",
    sortField: null,
    sortDirection: "asc",
  });

  const [supplierLogos, setSupplierLogos] = useState({});
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadNote, setUploadNote] = useState(null);
  const fileInputRef = React.useRef(null);

  const { getAccessTokenSilently, loginWithPopup, getAccessTokenWithPopup } =
    useAuth0();

  const handleFileClick = async (e, fileId) => {
    e.preventDefault();
    const receipt = state.receipts.find(r => r.id === fileId);
    if (receipt) await handleView(receipt);
  };

  // Auto-load organizations on component mount
  useEffect(() => {
    if (audience && ready) {
      callApi();
    }
    // callApi reads orgId; reload whenever the selected org changes
  }, [audience, ready, orgId]);

  // Load supplier logos
  useEffect(() => {
    const loadLogos = async () => {
      try {
        let token = null;
        try { token = await getAccessTokenSilently(); } catch (e) {}
        const data = await fetchSuppliers(token, orgId);
        const suppliers = data?.values || data?.Params || (Array.isArray(data) ? data : []);
        const logoMap = {};
        suppliers.forEach(s => {
          if (s.name && s.iconUrl) logoMap[s.name.toLowerCase()] = s.iconUrl;
        });
        setSupplierLogos(logoMap);
      } catch (e) {}
    };
    if (ready) loadLogos();
  }, [getAccessTokenSilently, ready, orgId]);

  // Filter receipts based on search term
  useEffect(() => {
    if (!state.searchTerm) {
      setState(prev => ({ ...prev, filteredReceipts: prev.receipts }));
    } else {
      const filtered = state.receipts.filter(receipt => 
        receipt.fileName?.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
        receipt.description?.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
        receipt.id?.toString().includes(state.searchTerm)
      );
      setState(prev => ({ ...prev, filteredReceipts: filtered }));
    }
  }, [state.receipts, state.searchTerm]);

  const handleSort = (field) => {
    const direction = state.sortField === field && state.sortDirection === "asc" ? "desc" : "asc";
    const sorted = [...state.filteredReceipts].sort((a, b) => {
      let aVal = a[field];
      let bVal = b[field];
      
      // Handle date sorting
      if (field === "createdOn_UTC") {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      
      // Handle null/undefined values
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      
      if (direction === "asc") {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
    
    setState(prev => ({
      ...prev,
      filteredReceipts: sorted,
      sortField: field,
      sortDirection: direction
    }));
  };

  const handleSearch = (e) => {
    setState(prev => ({ ...prev, searchTerm: e.target.value }));
  };

  const handleConsent = async () => {
    try {
      await getAccessTokenWithPopup();
      setState({
        ...state,
        error: null,
      });
    } catch (error) {
      setState({
        ...state,
        error: error.error,
      });
    }

    await callApi();
  };

  const handleLoginAgain = async () => {
    try {
      await loginWithPopup();
      setState({
        ...state,
        error: null,
      });
    } catch (error) {
      setState({
        ...state,
        error: error.error,
      });
    }

    await callApi();
  };

  const callApi = async () => {
    setState({
      ...state,
      loading: true,
      error: null,
    });

    try {
      let token = null;
      try {
        token = await getAccessTokenSilently();
      } catch (e) {
        token = null;
      }

      const apiData = await fetchReceipts(token, orgId);

      // Extract values from the response
      const receipts = apiData?.values || [];

      setState((prev) => ({
        ...prev,
        showResult: true,
        receipts: receipts,
        filteredReceipts: receipts,
        apiMessage: apiData,
        loading: false,
      }));
    } catch (error) {
      setState({
        ...state,
        error: error?.message || error?.error || "unknown_error",
        loading: false,
      });
    }
  };

  const handle = (e, fn) => {
    e.preventDefault();
    fn();
  };

  const downloadName = (receipt) => {
    let fileName = receipt.fileName || 'receipt';
    if (receipt.fileExtension) {
      const ext = receipt.fileExtension.startsWith('.') ? receipt.fileExtension : '.' + receipt.fileExtension;
      if (!fileName.toLowerCase().endsWith(ext.toLowerCase())) fileName += ext;
    }
    return fileName;
  };

  const loadFile = async (receipt) => {
    let token = null;
    try { token = await getAccessTokenSilently(); } catch (e) {}
    return fetchReceiptFile(receipt.id, token, orgId);
  };

  const handleView = async (receipt) => {
    // Open the tab now, while the click still counts as the user's, then point it at the file
    const tab = window.open('', '_blank');
    try {
      const blob = await loadFile(receipt);
      const url = URL.createObjectURL(blob);
      if (tab) tab.location.href = url;
      else window.location.href = url;
    } catch (e) {
      if (tab) tab.close();
      setState(prev => ({ ...prev, error: 'file_unavailable' }));
    }
  };

  const handleDownload = async (receipt) => {
    try {
      const blob = await loadFile(receipt);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadName(receipt);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setState(prev => ({ ...prev, error: 'file_unavailable' }));
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      let token = null;
      try { token = await getAccessTokenSilently(); } catch (e) {}
      await deleteReceipt(id, token, orgId);
      setState(prev => ({
        ...prev,
        receipts: prev.receipts.filter(r => r.id !== id),
        filteredReceipts: prev.filteredReceipts.filter(r => r.id !== id),
      }));
    } catch (e) {
      setState(prev => ({ ...prev, error: e.message || 'Failed to delete' }));
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadNote(null);
    try {
      let token = null;
      try { token = await getAccessTokenSilently(); } catch (err) {}
      const result = await uploadReceipts(files, token, orgId);
      const added = result?.added?.length || 0;
      const skipped = result?.skipped || [];
      setUploadNote({
        color: skipped.length > 0 ? 'warning' : 'success',
        text: `${added} receipt${added === 1 ? '' : 's'} added.` +
          (skipped.length > 0 ? ' Not added: ' + skipped.map(x => `${x.name} (${x.reason})`).join(', ') + '.' : ''),
      });
      await callApi();
    } catch (err) {
      setUploadNote({ color: 'danger', text: 'The receipts could not be added. Please try again.' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const ActionBtn = ({ onClick, variant, title, children }) => (
    <button onClick={onClick} title={title} className={`btn-pill btn-pill-${variant}`}>
      {children}
    </button>
  );

  const SortIcon = ({ field }) => {
    if (state.sortField !== field) return <span style={{ color: '#ced4da', marginLeft: '4px' }}>↕</span>;
    return <span style={{ color: '#007bff', marginLeft: '4px' }}>{state.sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <div style={{ paddingBottom: '3rem' }}>
      {/* Page header */}
      <div className="mb-4">
        <h2 className="page-title mb-1">Receipts</h2>
        <p className="page-subtitle">All retrieved receipts across your connected suppliers</p>
      </div>

      {state.error === "consent_required" && (
        <Alert color="warning" className="mb-3">
          You need to <a href="#/" className="alert-link" onClick={(e) => handle(e, handleConsent)}>consent to get access</a>.
        </Alert>
      )}
      {state.error === "login_required" && (
        <Alert color="warning" className="mb-3">
          You need to <a href="#/" className="alert-link" onClick={(e) => handle(e, handleLoginAgain)}>log in again</a>.
        </Alert>
      )}
      {uploadNote && (
        <Alert color={uploadNote.color} className="mb-3" toggle={() => setUploadNote(null)}>
          {uploadNote.text}
        </Alert>
      )}
      {state.error === "file_unavailable" && (
        <Alert color="warning" className="mb-3">
          That receipt's file could not be opened. Please try again in a moment.
        </Alert>
      )}
      {state.error && state.error !== "consent_required" && state.error !== "login_required" && state.error !== "file_unavailable" && (
        <Alert color="danger" className="mb-3">
          Something went wrong loading or changing your receipts. Please try again in a moment.
        </Alert>
      )}

      <Card style={{ border: '1px solid #e9ecef', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        {/* Table toolbar */}
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
          <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1a1d23' }}>
            {state.loading ? 'Loading...' : `${state.filteredReceipts.length} receipt${state.filteredReceipts.length !== 1 ? 's' : ''}`}
          </div>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              multiple
              style={{ display: 'none' }}
              onChange={handleUpload}
            />
            <button
              className="btn-pill btn-pill-blue"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              disabled={uploading || !ready}
            >
              {uploading ? 'Uploading…' : 'Upload receipts'}
            </button>
          </div>
          {state.receipts.length > 0 && (
            <div style={{ position: 'relative', maxWidth: '260px', width: '100%' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#adb5bd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <Input
                placeholder="Search receipts..."
                value={state.searchTerm}
                onChange={handleSearch}
                style={{ paddingLeft: '32px', fontSize: '0.85rem', border: '1px solid #e9ecef', borderRadius: '6px' }}
              />
            </div>
          )}
        </div>

        {state.loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#adb5bd', fontSize: '0.875rem' }}>
            <div className="spinner-border spinner-border-sm text-primary mb-2" role="status" />
            <div>Loading receipts...</div>
          </div>
        ) : (
          <Table responsive className="mb-0" style={{ fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ backgroundColor: '#fafbfc', borderBottom: '1px solid #e9ecef' }}>
                {[['id', 'ID', '60px'], ['description', 'Source', '260px'], ['createdOn_UTC', 'Date', '120px'], ['fileName', 'File', null]].map(([field, label, width]) => (
                  <th key={field}
                    onClick={() => handleSort(field)}
                    style={{ cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d', padding: '0.75rem 1.25rem', border: 'none', whiteSpace: 'nowrap', userSelect: 'none', ...(width ? { width, minWidth: width } : {}) }}
                  >
                    {label}<SortIcon field={field} />
                  </th>
                ))}
                <th style={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d', padding: '0.75rem 1.25rem', border: 'none', width: '120px' }} />
              </tr>
            </thead>
            <tbody>
              {state.filteredReceipts.length > 0 ? (
                state.filteredReceipts.map((receipt, i) => (
                  <tr key={receipt.id} style={{ borderBottom: '1px solid #f7f8fa', transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#fafbfc'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
                  >
                    <td style={{ padding: '0.85rem 1.25rem', color: '#adb5bd', fontWeight: 500, verticalAlign: 'middle' }}>
                      #{receipt.id}
                    </td>
                    <td style={{ padding: '0.85rem 1.25rem', verticalAlign: 'middle', width: '260px', minWidth: '260px' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '3px 10px 3px 6px', borderRadius: '20px', backgroundColor: 'var(--color-primary-light)', border: '1px solid #c8d8f5' }}>
                        {supplierLogos[receipt.description?.toLowerCase()] && (
                          <img
                            src={supplierLogos[receipt.description.toLowerCase()]}
                            alt={receipt.description}
                            style={{ width: '16px', height: '16px', objectFit: 'contain', borderRadius: '2px' }}
                          />
                        )}
                        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-primary)' }}>
                          {receipt.description || '—'}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '0.85rem 1.25rem', color: '#4a5568', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      {receipt.createdOn_UTC
                        ? new Date(receipt.createdOn_UTC).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                        : '—'}
                    </td>
                    <td style={{ padding: '0.85rem 1.25rem', verticalAlign: 'middle' }}>
                      {receipt.fileName ? (
                        <a href="#" onClick={(e) => handleFileClick(e, receipt.id, receipt.fileName, receipt.fileExtension)}
                          style={{ color: '#007bff', textDecoration: 'none', fontWeight: 500 }}
                          onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                          onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                        >
                          {receipt.fileName}
                        </a>
                      ) : (
                        <span style={{ color: '#ced4da' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '0.85rem 1.25rem', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      {confirmDeleteId === receipt.id ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>Delete?</span>
                          <button onClick={() => handleDelete(receipt.id)} disabled={deletingId === receipt.id} className="btn-pill btn-pill-red">
                            {deletingId === receipt.id ? '...' : 'Yes'}
                          </button>
                          <button onClick={() => setConfirmDeleteId(null)} className="btn-pill btn-pill-grey">
                            No
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <ActionBtn title="View" variant="blue" onClick={() => handleView(receipt)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                            View
                          </ActionBtn>
                          <ActionBtn title="Download" variant="green" onClick={() => handleDownload(receipt)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                            Download
                          </ActionBtn>
                          <ActionBtn title="Delete" variant="red" onClick={() => setConfirmDeleteId(receipt.id)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                            Delete
                          </ActionBtn>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" style={{ padding: '4rem', textAlign: 'center' }}>
                    {state.searchTerm ? (
                      <>
                        <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>🔍</div>
                        <div style={{ fontWeight: 600, color: '#343a40', marginBottom: '0.25rem' }}>No results for "{state.searchTerm}"</div>
                        <div style={{ fontSize: '0.82rem', color: '#adb5bd' }}>Try a different search term</div>
                      </>
                    ) : state.error ? (
                      <div style={{ fontWeight: 600, color: '#343a40' }}>Receipts could not be loaded</div>
                    ) : (
                      <>
                        <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>📄</div>
                        <div style={{ fontWeight: 600, color: '#343a40', marginBottom: '0.25rem' }}>No receipts yet</div>
                        <div style={{ fontSize: '0.82rem', color: '#adb5bd', marginBottom: '1rem' }}>Connect a supplier to start retrieving receipts</div>
                        <Link to="/suppliers/1" className="btn-pill btn-pill-blue">Add Supplier</Link>
                      </>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
};

export default withAuthenticationRequired(ReceiptsComponent, {
  onRedirecting: () => <Loading />,
});

